import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import type { ConceptSearchBrief } from "fin-web-components";
import { filterConceptsByKeyword } from "fin-web-components";
import { H5BackButton } from "../../components/common/H5BackButton";
import { h5BackButtonCssVars } from "../../components/common/H5BackButtonConfig";
import "../../components/common/H5BackButton.css";
import { CONCEPT_MAX_SELECTION, conceptSearchCssVars } from "./Config";
import { readRecentConcepts, recordRecentConcept } from "./ConceptRecentLocalStore";
import ConceptSelectedBar from "./ConceptSelectedBar";
import type { ConceptBrief } from "./ConceptType";
import "./ConceptSearchSheet.css";

type Props = {
  open: boolean;
  spaceProductId: string;
  catalog: ConceptBrief[];
  initialCodes: string[];
  onClose: () => void;
  onConfirm: (codes: string[]) => void;
};

function briefFromConcept(c: ConceptBrief): ConceptSearchBrief {
  return {
    code: c.code,
    name: c.name,
    pinYin: c.pinYin,
    pinYinInitial: c.pinYinInitial,
  };
}

function resolveConceptFromCatalog(catalog: ConceptBrief[], code: string): ConceptBrief | undefined {
  return catalog.find((c) => c.code === code);
}

interface ConceptSearchRowProps {
  item: ConceptSearchBrief;
  checked: boolean;
  onSelect: (code: string) => void;
  recent?: boolean;
}

function ConceptSearchRow(props: ConceptSearchRowProps): JSX.Element {
  const { item, checked, onSelect, recent = false } = props;
  const rowClass = [
    "lf-concept-search__row",
    recent ? "lf-concept-search__row--recent" : null,
    checked ? "lf-concept-search__row--checked" : null,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <li>
      <button type="button" className={rowClass} onClick={() => onSelect(item.code)}>
        <span className="lf-concept-search__row-main">
          <span className="lf-concept-search__row-name">{item.name}</span>
          <span className="lf-concept-search__row-code">{item.code}</span>
        </span>
        {checked ? (
          <span className="lf-concept-search__check" aria-hidden>
            ✓
          </span>
        ) : null}
      </button>
    </li>
  );
}

export default function ConceptSearchSheet(props: Props): JSX.Element | null {
  const { open, spaceProductId, catalog, initialCodes, onClose, onConfirm } = props;
  const [keyword, setKeyword] = useState("");
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [recentConcepts, setRecentConcepts] = useState<ConceptBrief[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setKeyword("");
    setSelectedCodes([...initialCodes]);
    setRecentConcepts(readRecentConcepts(spaceProductId));
    const t = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, [open, initialCodes, spaceProductId]);

  const catalogBriefs = useMemo(() => catalog.map(briefFromConcept), [catalog]);

  const filtered = useMemo(
    () => filterConceptsByKeyword(catalogBriefs, keyword, 50),
    [catalogBriefs, keyword]
  );

  const recentBriefs = useMemo(() => {
    const catalogByCode = new Map(catalog.map((c) => [c.code, c]));
    const out: ConceptSearchBrief[] = [];
    for (const item of recentConcepts) {
      const hit = catalogByCode.get(item.code) ?? item;
      out.push(briefFromConcept(hit));
    }
    return out;
  }, [catalog, recentConcepts]);

  const keywordTrimmed = keyword.trim();
  const showRecent = keywordTrimmed.length === 0 && recentBriefs.length > 0;

  const selectedBriefs = useMemo(() => {
    const out: ConceptBrief[] = [];
    for (const code of selectedCodes) {
      const hit = catalog.find((c) => c.code === code);
      if (hit) {
        out.push(hit);
      }
    }
    return out;
  }, [catalog, selectedCodes]);

  const selectCode = useCallback(
    (code: string) => {
      setSelectedCodes((prev) => {
        if (prev.includes(code)) {
          return prev.filter((c) => c !== code);
        }
        if (prev.length >= CONCEPT_MAX_SELECTION) {
          message.warning(`最多选择${CONCEPT_MAX_SELECTION}个概念`);
          return prev;
        }
        const concept = resolveConceptFromCatalog(catalog, code);
        if (concept) {
          recordRecentConcept(spaceProductId, concept);
          setRecentConcepts(readRecentConcepts(spaceProductId));
        }
        return [...prev, code];
      });
      setKeyword("");
      window.requestAnimationFrame(() => inputRef.current?.focus());
    },
    [catalog, spaceProductId]
  );

  const removeCode = useCallback((code: string) => {
    setSelectedCodes((prev) => prev.filter((c) => c !== code));
  }, []);

  const handleConfirm = useCallback(() => {
    if (selectedCodes.length === 0) {
      return;
    }
    onConfirm(selectedCodes);
    onClose();
  }, [onClose, onConfirm, selectedCodes]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="lf-concept-search"
      style={{ ...h5BackButtonCssVars(), ...conceptSearchCssVars() }}
      role="dialog"
      aria-modal="true"
      aria-label="选择概念"
    >
      <header className="lf-concept-search__header">
        <H5BackButton
          className="lf-concept-search__back-btn"
          iconClassName="h5-back-button__icon h5-back-button__icon--invert"
          onClick={onClose}
        />
        <div className="lf-concept-search__input-wrap">
          <input
            ref={inputRef}
            className="lf-concept-search__input"
            type="search"
            enterKeyHint="search"
            placeholder="概念名称/拼音/代码"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && selectedCodes.length > 0) {
                e.preventDefault();
                handleConfirm();
              }
            }}
          />
        </div>
        <button
          type="button"
          className="lf-concept-search__search-btn"
          disabled={selectedCodes.length === 0}
          onClick={handleConfirm}
        >
          搜索
        </button>
      </header>

      <ConceptSelectedBar concepts={selectedBriefs} onRemove={removeCode} />

      <ul className="lf-concept-search__list">
        {showRecent ? (
          <>
            <li className="lf-concept-search__section-title">最近选择</li>
            {recentBriefs.map((item) => (
              <ConceptSearchRow
                key={`recent-${item.code}`}
                item={item}
                checked={selectedCodes.includes(item.code)}
                onSelect={selectCode}
                recent
              />
            ))}
          </>
        ) : null}
        {!showRecent && filtered.length === 0 ? (
          <li className="lf-concept-search__empty">
            {keywordTrimmed.length === 0 ? "输入关键词搜索概念" : "无匹配概念"}
          </li>
        ) : null}
        {keywordTrimmed.length > 0
          ? filtered.map((item) => (
              <ConceptSearchRow
                key={item.code}
                item={item}
                checked={selectedCodes.includes(item.code)}
                onSelect={selectCode}
              />
            ))
          : null}
      </ul>
    </div>
  );
}
